/**
 * Форматирование денег и процентов.
 *
 * Правило ТЗ №5 действует и в админке: суммы приходят готовыми строками, здесь
 * они только форматируются. Единственное исключение — поле ввода корректировки
 * баланса, и оно уходит на бэкенд строкой, ровно как её ввёл оператор.
 *
 * `Intl.NumberFormat` не используется: он принимает `number`, а
 * `Number("12345678901234.56")` уже теряет копейки. Разряды режутся строкой,
 * поэтому `decimal.js` админке не нужен вовсе.
 */

/** Ответ бэкенда: ровно два знака, `^-?\d+\.\d{2}$`. */
export type Money = string;

/** Узкий неразрывный пробел: разряды не разрываются переносом. */
const GROUP_SEPARATOR = "\u202f";

function groupDigits(whole: string): string {
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);
}

function split(value: string): { negative: boolean; digits: string } {
  const trimmed = value.trim();
  const negative = trimmed.startsWith("-");
  return { negative, digits: negative ? trimmed.slice(1) : trimmed };
}

export function formatMoney(
  value: Money,
  options: { sign?: "auto" | "always"; suffix?: string | null } = {},
): string {
  const { sign = "auto", suffix = null } = options;
  const { negative, digits } = split(value);
  const [whole = "0", fraction = "00"] = digits.split(".");

  // «−» (U+2212), а не дефис: в таблице колонка сумм выравнивается по знаку,
  // и дефис визуально короче минуса в цифровом шрифте.
  const prefix = negative ? "−" : sign === "always" ? "+" : "";
  const body = `${groupDigits(whole)},${fraction}`;
  return suffix ? `${prefix}${body}${GROUP_SEPARATOR}${suffix}` : `${prefix}${body}`;
}

/**
 * Проценты. Точность источника сохраняется как есть: дневной результат бэкенд
 * сознательно отдаёт с тремя знаками, а витринные показатели — с двумя.
 */
export function formatPercent(value: string, options: { sign?: "auto" | "always" } = {}): string {
  const { sign = "auto" } = options;
  const { negative, digits } = split(value);
  const prefix = negative ? "−" : sign === "always" ? "+" : "";
  return `${prefix}${digits.replace(".", ",")}${GROUP_SEPARATOR}%`;
}

/** Знак суммы для раскраски: положительное — зелёное, отрицательное — красное. */
export function signOf(value: string): "positive" | "negative" | "zero" {
  const { negative, digits } = split(value);
  if (/^0+([.,]0+)?$/.test(digits)) return "zero";
  return negative ? "negative" : "positive";
}
